import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { ADD_FAVORITE, REMOVE_FAVORITE } from '../../services/apollo/mutations/favoriteMutations';
import { GET_USER_FAVORITES } from '../../services/apollo/queries/favoriteQueries';
import { fetchFavorites, addFavorite, removeFavorite } from '../../slices/favoritesSlice';

const useFavorites = () => {
  const dispatch = useDispatch();
  const favorites = useSelector((state) => state.favorites);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  // Memoized utility to check if an item is a favorite
  const isFavorite = useCallback(
    (itemId) => favorites.allIds.includes(itemId),
    [favorites.allIds]
  );

  const { loading, error, data } = useQuery(GET_USER_FAVORITES, {
    skip: !isAuthenticated,
  });

  useEffect(() => {
    if (data?.userFavorites) {
      dispatch(fetchFavorites(data.userFavorites));
    }
  }, [data, dispatch]);

  // Handle query errors
  useEffect(() => {
    if (error) {
      console.error('Error fetching user favorites:', error.message);
    }
  }, [error]);

  const [addFavoriteMutation] = useMutation(ADD_FAVORITE, {
    refetchQueries: [{ query: GET_USER_FAVORITES }],
  });

  const [removeFavoriteMutation] = useMutation(REMOVE_FAVORITE, {
    refetchQueries: [{ query: GET_USER_FAVORITES }],
  });

  const addToFavorites = async (input) => {
    try {
      const { data } = await addFavoriteMutation({ variables: { input } });
      if (data?.addFavorite) {
        dispatch(addFavorite(data.addFavorite));
      } else {
        console.warn('Add favorite mutation did not return data.');
      }
    } catch (err) {
      console.error('Unexpected error adding to favorites:', err);
    }
  };

  const removeFromFavorites = async (itemId) => {
    try {
      const { data } = await removeFavoriteMutation({ variables: { itemId } });
      if (data) {
        dispatch(removeFavorite(itemId));
      } else {
        console.warn('Remove favorite mutation did not return data.');
      }
    } catch (err) {
      console.error('Unexpected error removing from favorites:', err);
    }
  };

  return {
    favorites,
    isFavorite,
    loading,
    error,
    addToFavorites,
    removeFromFavorites,
  };
};

export default useFavorites;
